import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { useTranslation } from "react-i18next";
import ColumnPicker from "./ColumnPicker";
import { useColumnVisibility } from "./useColumnVisibility";
import { ColumnDef, DataTableProps, Order } from "./types";

function resolveSortValue<T>(column: ColumnDef<T>, row: T): string | number {
  if (column.sortValue) return column.sortValue(row);
  const raw = (row as Record<string, unknown>)[column.id];
  if (typeof raw === "number" || typeof raw === "string") return raw;
  return "";
}

/**
 * The single table used by every admin list screen.
 *
 * SuperAdmin and RM render THIS component with the SAME column definitions. The
 * only difference between the two roles is which rows the page fetched — role
 * scoping is a data concern, enforced server-side, and deliberately invisible
 * here. Do not add a role prop; a role branch inside this component is exactly
 * the duplication it exists to remove.
 */
export default function DataTable<T>({
  tableId,
  columns,
  rows,
  getRowId,
  onRowClick,
  emptyLabelKey,
  initialOrderBy,
  initialOrder = "asc",
  rowsPerPageOptions = [5, 10, 25],
  hideColumnPicker = false,
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const { visibleColumns, isVisible, toggleColumn, resetColumns } =
    useColumnVisibility(tableId, columns);

  const [order, setOrder] = React.useState<Order>(initialOrder);
  const [orderBy, setOrderBy] = React.useState<string>(
    initialOrderBy ?? columns[0]?.id ?? ""
  );
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(rowsPerPageOptions[0]);

  const handleSort = (columnId: string) => {
    const isAsc = orderBy === columnId && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnId);
  };

  const sortedRows = React.useMemo(() => {
    const column = columns.find((c) => c.id === orderBy);
    if (!column || column.sortable === false) return rows;
    const factor = order === "asc" ? 1 : -1;
    // Copy first — Array.prototype.sort mutates, and `rows` is the caller's state.
    return [...rows].sort((a, b) => {
      const av = resolveSortValue(column, a);
      const bv = resolveSortValue(column, b);
      if (typeof av === "number" && typeof bv === "number") {
        return (av - bv) * factor;
      }
      return String(av).localeCompare(String(bv)) * factor;
    });
  }, [rows, columns, order, orderBy]);

  const visibleRows = React.useMemo(
    () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedRows, page, rowsPerPage]
  );

  // A filter change can strand the viewer on a page that no longer exists,
  // which renders an empty table over a non-empty result set.
  React.useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [rows.length, rowsPerPage, page]);

  return (
    <Paper className="w-full mb-2">
      {!hideColumnPicker && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1, pt: 1 }}>
          <ColumnPicker
            columns={columns}
            isVisible={isVisible}
            onToggle={toggleColumn}
            onReset={resetColumns}
          />
        </Box>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {visibleColumns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align ?? "left"}
                  sortDirection={orderBy === column.id ? order : false}
                >
                  {column.sortable === false ? (
                    t(column.labelKey)
                  ) : (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={() => handleSort(column.id)}
                    >
                      {t(column.labelKey)}
                      {orderBy === column.id && (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </Box>
                      )}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {visibleRows.length > 0 ? (
              visibleRows.map((row) => {
                const id = getRowId(row);
                return (
                  <TableRow
                    hover
                    key={id}
                    tabIndex={-1}
                    onClick={onRowClick ? () => onRowClick(id) : undefined}
                    sx={onRowClick ? { cursor: "pointer" } : undefined}
                  >
                    {visibleColumns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align ?? "left"}
                        onClick={
                          column.stopRowClick
                            ? (e) => e.stopPropagation()
                            : undefined
                        }
                      >
                        {column.render(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={Math.max(1, visibleColumns.length)}
                  align="center"
                  sx={{ py: 4 }}
                >
                  <Typography color="text.secondary">
                    {t(emptyLabelKey)}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        sx={{
          "& .MuiTablePagination-toolbar": { flexWrap: "wrap" },
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
            { "@media (max-width: 640px)": { fontSize: "0.75rem" } },
        }}
      />
    </Paper>
  );
}
